/**
 * ExcelJS-based `.xlsx` generation + download (AUD-007).
 *
 * Replaces SheetJS `writeFile` for template/export flows. Values are written
 * as literal scalars only — no formulas — and strings that a spreadsheet
 * application would interpret as a formula (`=`, `+`, `-`, `@` prefix) are
 * escaped with a leading apostrophe to prevent formula injection in the
 * downloaded file.
 */

export type ExportCell = string | number | boolean | null | undefined;

export interface SheetSpec {
  name: string;
  /** Rows of scalar cells; include the header row explicitly. */
  rows: ExportCell[][];
  /** Optional column widths in characters, in column order. */
  colWidths?: number[];
}

const FORMULA_PREFIX = /^[=+\-@]/;

/** Escape strings a spreadsheet app would evaluate as a formula. */
export function sanitizeCellValue(value: ExportCell): string | number | boolean {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' && FORMULA_PREFIX.test(value)) return `'${value}`;
  return value;
}

/**
 * Build a workbook from sheet specs and trigger a browser download.
 * ExcelJS is imported dynamically to keep it out of the initial bundle.
 */
export async function downloadXlsx(filename: string, sheets: SheetSpec[]): Promise<void> {
  const mod = await import('exceljs');
  const ExcelJS = mod.default ?? mod;

  const workbook = new ExcelJS.Workbook();
  for (const spec of sheets) {
    const worksheet = workbook.addWorksheet(spec.name);
    if (spec.colWidths) {
      worksheet.columns = spec.colWidths.map((width) => ({ width }));
    }
    for (const row of spec.rows) {
      worksheet.addRow(row.map(sanitizeCellValue));
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
